import type { ScopeIntent } from './types';

/** Decode exactly once. A malformed route is invalid, never an implicit default. */
export function decodedPathIdentity(segment: string): string | null {
  try { return decodeURIComponent(segment); } catch { return null; }
}

/** The old recovery source query is a route alias, never a second selector. */
export function parseRouteScopeIntent(pathname: string, search: string, aliases: Readonly<Record<string, string>> = {}): ScopeIntent {
  let ordinary = parseScopeIntent(search, aliases);
  if (pathname.replace(/\/$/, '') === '/administration/commercial/cost-explorer') {
    const historical = new URLSearchParams(search).getAll('environment');
    if (historical.length) {
      if (historical.length !== 1 || !historical[0].trim()) return {kind:'invalid',reason:'invalid-cost-explorer-legacy-scope'};
      const raw=historical[0];
      // Historical selector emitted lowercase keys; accept case-equivalent registered aliases only.
      const exact=Object.prototype.hasOwnProperty.call(aliases,raw)?aliases[raw]:null;
      const matches=[...new Set(Object.entries(aliases).filter(([key])=>key.toLowerCase()===raw.toLowerCase()).map(([,id])=>id))];
      const id=exact??(matches.length===1?matches[0]:null);
      if(!id)return {kind:'invalid',reason:'unmapped-cost-explorer-legacy-scope'};
      if(ordinary.kind==='invalid')return ordinary;
      if(ordinary.kind==='unspecified')ordinary={kind:'environment',environmentId:id};
      else if(ordinary.kind!=='environment'||(aliases[ordinary.environmentId]??ordinary.environmentId)!==id)return {kind:'invalid',reason:'conflicting-cost-explorer-scope'};
      else ordinary={kind:'environment',environmentId:id};
    }
  }
  if (!['/backups', '/backup-restore', '/recovery'].includes(pathname)) return ordinary;
  const params = new URLSearchParams(search), legacy = params.getAll('recovery_env');
  if (!legacy.length) return ordinary;
  if (legacy.length !== 1 || !legacy[0].trim()) return { kind: 'invalid', reason: 'invalid-legacy-recovery-scope' };
  const id = Object.prototype.hasOwnProperty.call(aliases, legacy[0]) ? aliases[legacy[0]] : null;
  if (!id) return { kind: 'invalid', reason: 'unmapped-legacy-recovery-scope' };
  if (ordinary.kind === 'invalid') return ordinary;
  if (ordinary.kind === 'unspecified') return { kind: 'environment', environmentId: id };
  if (ordinary.kind === 'environment' && (aliases[ordinary.environmentId] ?? ordinary.environmentId) === id) return { kind: 'environment', environmentId: id };
  return { kind: 'invalid', reason: 'conflicting-recovery-source-scope' };
}

/** New contract: environmentScope=all is explicit; search owns the scope query key.
 * Legacy aliases must be supplied from a reviewed mapping, never name stripping. */
export function parseScopeIntent(search: string, aliases: Readonly<Record<string, string>> = {}): ScopeIntent {
  const params = new URLSearchParams(search);
  for (const key of ['environmentScope', 'environmentId', 'env']) {
    if (params.getAll(key).length > 1) return { kind: 'invalid', reason: 'duplicate' };
  }
  const mode = params.get('environmentScope');
  const canonical = params.get('environmentId');
  const legacy = params.get('env');
  if (mode !== null && mode !== 'all') return { kind: 'invalid', reason: 'unknown-mode' };
  if (mode === 'all') return canonical !== null || legacy !== null
    ? { kind: 'invalid', reason: 'conflict' } : { kind: 'all' };
  if (canonical === '' || legacy === '' || canonical?.trim() === '' || legacy?.trim() === '') {
    return { kind: 'invalid', reason: 'empty' };
  }
  const mapped = legacy === null ? null : Object.prototype.hasOwnProperty.call(aliases, legacy) ? aliases[legacy] : null;
  if (legacy !== null && !mapped) return { kind: 'invalid', reason: 'unmapped-legacy-identity' };
  if (canonical !== null && mapped !== null && canonical !== mapped) return { kind: 'invalid', reason: 'conflict' };
  const id = canonical ?? mapped;
  return id === null ? { kind: 'unspecified' } : { kind: 'environment', environmentId: id };
}
